import { create } from 'zustand';
import { validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';

export interface TeamBioPhoto {
  url: string;
  formats?: {
    thumbnail?: { url: string };
    small?: { url: string };
  };
}

export interface TeamBio {
  id: number;
  documentId: string;
  name: string;
  title: string;
  team: 'staff' | 'board';
  bio: string;
  photo?: TeamBioPhoto | null;
  createdAt: string;
}

interface TeamBioStore {
  teamBios: TeamBio[];
  loading: boolean;
  fetchTeamBios: () => Promise<void>;
  createTeamBio: (data: {
    name: string;
    title: string;
    team: 'staff' | 'board';
    bio: string;
    photo?: File | null;
  }) => Promise<TeamBio | null>;
  updateTeamBio: (documentId: string, data: {
    name?: string;
    title?: string;
    team?: 'staff' | 'board';
    bio?: string;
    photo?: File | null | undefined;
  }) => Promise<TeamBio | null>;
  deleteTeamBio: (documentId: string) => Promise<boolean>;
}

const baseUrl = '/api/strapi';

async function uploadPhoto(file: File): Promise<number | null> {
  validateFileSize(file);
  const formData = new FormData();
  formData.append('files', file);
  const res = await fetch(`${baseUrl}/upload`, { method: 'POST', body: formData });
  if (!res.ok) return null;
  const files = await res.json();
  return files[0]?.id ?? null;
}

export const useTeamBioStore = create<TeamBioStore>((set, get) => ({
  teamBios: [],
  loading: false,

  fetchTeamBios: async () => {
    set({ loading: true });
    try {
      const teamBios = await fetchStrapiCollection<TeamBio>('team-bios', {
        populate: 'photo',
        sort: 'createdAt:asc',
      });
      set({ teamBios });
    } catch (err) {
      console.error('Error fetching team bios:', err);
    } finally {
      set({ loading: false });
    }
  },

  createTeamBio: async ({ name, title, team, bio, photo }) => {
    try {
      let photoId: number | null = null;
      if (photo) photoId = await uploadPhoto(photo);

      const payload: Record<string, unknown> = { name, title, team, bio };
      if (photoId) payload.photo = photoId;

      const res = await fetch(`${baseUrl}/team-bios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) return null;
      const result = await res.json();
      await get().fetchTeamBios();
      return result.data ?? null;
    } catch (err) {
      console.error('Error creating team bio:', err);
      return null;
    }
  },

  updateTeamBio: async (documentId, { name, title, team, bio, photo }) => {
    try {
      let photoId: number | null = null;
      if (photo instanceof File) photoId = await uploadPhoto(photo);

      const payload: Record<string, unknown> = {};
      if (name !== undefined) payload.name = name;
      if (title !== undefined) payload.title = title;
      if (team !== undefined) payload.team = team;
      if (bio !== undefined) payload.bio = bio;
      // photo === undefined: unchanged — omit from payload
      // photo === null: user removed it — clear the relation
      // photo instanceof File: new upload — set to uploaded id
      if (photo === null) {
        payload.photo = null;
      } else if (photoId) {
        payload.photo = photoId;
      }

      const res = await fetch(`${baseUrl}/team-bios/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) return null;
      const result = await res.json();
      await get().fetchTeamBios();
      return result.data ?? null;
    } catch (err) {
      console.error('Error updating team bio:', err);
      return null;
    }
  },

  deleteTeamBio: async (documentId) => {
    try {
      const res = await fetch(`${baseUrl}/team-bios/${documentId}`, { method: 'DELETE' });
      if (res.ok) {
        set((state) => ({ teamBios: state.teamBios.filter((b) => b.documentId !== documentId) }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error deleting team bio:', err);
      return false;
    }
  },
}));
